// ===========================================
// konakla - Supabase Realtime Subscriptions
// ===========================================

import { createClient } from "./client";
import type { RealtimeChannel } from "@supabase/supabase-js";
import type { Database } from "@/types/database";

type Tables = Database["public"]["Tables"];
type ListingView = Tables["listing_views"]["Row"];
type ContactClick = Tables["contact_clicks"]["Row"];
type Listing = Tables["listings"]["Row"];

// Subscription manager sınıfı
class RealtimeManager {
  private channels: Map<string, RealtimeChannel> = new Map();

  // Channel oluştur veya mevcut olanı getir
  private getOrCreateChannel(channelName: string): RealtimeChannel | null {
    const supabase = createClient();
    if (!supabase) return null;

    if (this.channels.has(channelName)) {
      return this.channels.get(channelName)!;
    }

    const channel = supabase.channel(channelName);
    this.channels.set(channelName, channel);
    return channel;
  }

  // Channel'ı temizle
  private removeChannel(channelName: string): void {
    const channel = this.channels.get(channelName);
    if (channel) {
      channel.unsubscribe();
      this.channels.delete(channelName);
    }
  }

  // ===================================
  // LISTING VIEWS SUBSCRIPTIONs
  // ===================================

  /**
   * Belirli bir ilanın görüntüleme sayısını dinle
   */
  subscribeToListingViews(listingId: string, onNewView: (view: ListingView) => void): () => void {
    const channelName = `listing-views:${listingId}`;
    const channel = this.getOrCreateChannel(channelName);

    if (!channel) {
      console.warn("Realtime not available");
      return () => {};
    }

    channel
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "listing_views",
          filter: `listing_id=eq.${listingId}`,
        },
        (payload) => {
          onNewView(payload.new as ListingView);
        }
      )
      .subscribe();

    // Cleanup function
    return () => this.removeChannel(channelName);
  }

  // ===================================
  // CONTACT CLICKS SUBSCRIPTIONS
  // ===================================

  /**
   * Belirli bir ilanın iletişim tıklamalarını dinle
   */
  subscribeToContactClicks(
    listingId: string,
    onNewClick: (click: ContactClick) => void
  ): () => void {
    const channelName = `contact-clicks:${listingId}`;
    const channel = this.getOrCreateChannel(channelName);

    if (!channel) {
      console.warn("Realtime not available");
      return () => {};
    }

    channel
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "contact_clicks",
          filter: `listing_id=eq.${listingId}`,
        },
        (payload) => {
          onNewClick(payload.new as ContactClick);
        }
      )
      .subscribe();

    return () => this.removeChannel(channelName);
  }

  // ===================================
  // OWNER DASHBOARD SUBSCRIPTIONS
  // ===================================

  /**
   * Owner'ın tüm ilanlarının aktivitelerini dinle
   */
  subscribeToOwnerActivity(
    ownerId: string,
    callbacks: {
      onView?: (view: ListingView) => void;
      onClick?: (click: ContactClick) => void;
      onListingChange?: (listing: Listing, event: "INSERT" | "UPDATE" | "DELETE") => void;
    }
  ): () => void {
    const supabase = createClient();
    if (!supabase) {
      console.warn("Realtime not available");
      return () => {};
    }

    const channelName = `owner-activity:${ownerId}`;
    const channel = this.getOrCreateChannel(channelName);

    if (!channel) return () => {};

    // Listing changes
    if (callbacks.onListingChange) {
      channel.on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "listings",
          filter: `owner_id=eq.${ownerId}`,
        },
        (payload) => {
          const event = payload.eventType as "INSERT" | "UPDATE" | "DELETE";
          const listing = (event === "DELETE" ? payload.old : payload.new) as Listing;
          callbacks.onListingChange!(listing, event);
        }
      );
    }

    channel.subscribe();

    return () => this.removeChannel(channelName);
  }

  // ===================================
  // ADMIN SUBSCRIPTIONS
  // ===================================

  /**
   * Yeni pending ilanları dinle (admin için)
   */
  subscribeToPendingListings(onNewPending: (listing: Listing) => void): () => void {
    const channelName = "admin:pending-listings";
    const channel = this.getOrCreateChannel(channelName);

    if (!channel) {
      console.warn("Realtime not available");
      return () => {};
    }

    channel
      .on(
        "postgres_changes",
        {
          event: "INSERT",
          schema: "public",
          table: "listings",
          filter: "status=eq.pending",
        },
        (payload) => {
          onNewPending(payload.new as Listing);
        }
      )
      .subscribe();

    return () => this.removeChannel(channelName);
  }

  /**
   * İlan durumu değişikliklerini dinle (admin için)
   */
  subscribeToListingStatusChanges(
    onStatusChange: (listing: Listing, oldStatus: string) => void
  ): () => void {
    const channelName = "admin:listing-status";
    const channel = this.getOrCreateChannel(channelName);

    if (!channel) {
      console.warn("Realtime not available");
      return () => {};
    }

    channel
      .on(
        "postgres_changes",
        {
          event: "UPDATE",
          schema: "public",
          table: "listings",
        },
        (payload) => {
          const oldListing = payload.old as Partial<Listing>;
          const newListing = payload.new as Listing;

          if (oldListing.status !== newListing.status) {
            onStatusChange(newListing, oldListing.status || "unknown");
          }
        }
      )
      .subscribe();

    return () => this.removeChannel(channelName);
  }

  // ===================================
  // CLEANUP
  // ===================================

  /**
   * Tüm subscription'ları temizle
   */
  unsubscribeAll(): void {
    this.channels.forEach((channel) => {
      channel.unsubscribe();
    });
    this.channels.clear();
  }
}

// Singleton instance
let realtimeManager: RealtimeManager | null = null;

export function getRealtimeManager(): RealtimeManager {
  if (!realtimeManager) {
    realtimeManager = new RealtimeManager();
  }
  return realtimeManager;
}

// Convenience exports
export const subscribeToListingViews = (
  listingId: string,
  onNewView: (view: ListingView) => void
) => getRealtimeManager().subscribeToListingViews(listingId, onNewView);

export const subscribeToContactClicks = (
  listingId: string,
  onNewClick: (click: ContactClick) => void
) => getRealtimeManager().subscribeToContactClicks(listingId, onNewClick);

export const subscribeToOwnerActivity = (
  ownerId: string,
  callbacks: Parameters<RealtimeManager["subscribeToOwnerActivity"]>[1]
) => getRealtimeManager().subscribeToOwnerActivity(ownerId, callbacks);

export const subscribeToPendingListings = (onNewPending: (listing: Listing) => void) =>
  getRealtimeManager().subscribeToPendingListings(onNewPending);

export const subscribeToListingStatusChanges = (
  onStatusChange: (listing: Listing, oldStatus: string) => void
) => getRealtimeManager().subscribeToListingStatusChanges(onStatusChange);

export const unsubscribeAll = () => getRealtimeManager().unsubscribeAll();
